import React, { useState, useEffect, Fragment } from "react";
import { Popover } from "@headlessui/react";
import { StaticQuery, graphql, Link } from "gatsby";
// Gatsby's own router package (gatsby-link requires this exact specifier), so
// the Location context this reads is the one Gatsby provides — <ServerLocation>
// during the static build and the browser history after hydration. Reading
// window.location instead would render the active marker only on the client and
// mismatch the server HTML on hydrate.
import { useLocation } from "@gatsbyjs/reach-router";
import MenuItemDropdown, { resolveActiveItemId } from "./MenuItemDropdown";
import ContactFormModal from "./ContactFormModal";
import Logo from "./logo-svg";
import Button from "./atoms/Button";

import { PhoneCall, X } from "react-feather";
import { ArrowRight, BadgeCheck, ChevronRight, ChevronUp, ListOrdered, Menu, Phone, PhoneIcon, Target, TimerReset, TrendingUp, Users2 } from "lucide-react";
import USAFlag from "../assets/icons/usa-flag";
import siteConfig from "../siteConfig";
import { siteHref } from "../helpers/siteUrls";
export const USAICON = USAFlag;


export const featureList = [
  {
    item: (
      <>
        Family Owned
        <span className="hidden md:inline"> & Operated</span>
      </>
    ),
    icon: Users2,
  },
  {
    item: (
      <>
        Licensed & Insured
      </>
    ),
    icon: BadgeCheck,
  },
  {
    item: (
      <>
        Free Estimates
      </>
    ),
    icon: ListOrdered,
  },
]


// Contentful owns the Main Navigation entry, so this item belongs there — but the
// Contentful -> Vercel rebuild path is not wired, meaning a CMS-only edit would sit
// unpublished until some unrelated commit happened to trigger a deploy. Carrying the
// item in code ships it through the one path that is known to work. withServiceAreas
// matches on the url, so the moment the entry is added to Contentful the injection
// stops firing on its own and no duplicate ever renders.
const SERVICE_AREAS_ITEM = {
  id: "local-service-areas",
  name: "Service Areas",
  url: "/areas-we-serve/",
};

const normalizeMenuUrl = (url) =>
  String(url || "")
    .trim()
    .toLowerCase()
    .replace(/\/+$/, "");

const withServiceAreas = (menuItems) => {
  const items = menuItems || [];
  const target = normalizeMenuUrl(SERVICE_AREAS_ITEM.url);

  if (items.some((item) => normalizeMenuUrl(item.url) === target)) {
    return items;
  }

  // Located by url rather than index so a reorder in the CMS cannot strand it.
  const servicesIndex = items.findIndex((item) => normalizeMenuUrl(item.url) === "/services");
  const next = [...items];
  next.splice(servicesIndex < 0 ? next.length : servicesIndex + 1, 0, SERVICE_AREAS_ITEM);
  return next;
};

// Contentful's menu urls are typed by hand: "Pricing" still carries the roof
// calculator's old url, and "Financing" has no trailing slash, so it 301s on
// every page three times over. Same reasoning as SERVICE_AREAS_ITEM: the
// correction belongs in the CMS, but only a code change is sure to ship.
// Dropdown children are covered too, so filing an item under a menu in the CMS
// cannot bring a bad url back.
const withSiteUrls = (menuItems) =>
  (menuItems || []).map((item) => ({
    ...item,
    url: siteHref(item.url),
    submenu:
      item.submenu &&
      item.submenu.map((group) => group && { ...group, menuItems: withSiteUrls(group.menuItems) }),
  }));

function Navbar({ headerTransparent = false, shadow = true }) {
  const companyname = "Roach Home Improvement";
  const slogan = "#1 Roofing Experts Serving Michigan";
  const subtagline = "20+ years of serving communities";
  // Header shows the main company line on every page, including city pages.
  const phonenumber = siteConfig.phone;
  const phoneHref = `tel:${siteConfig.phoneE164(phonenumber)}`;
  const mainnavigationstyle = "inline";

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contactFormOpen, setContactFormOpen] = useState(false);

  const { pathname } = useLocation();

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add("menu-expanded");
    }

    return function cleanup() {
      document.body.classList.remove("menu-expanded");
    };
  }, [mobileMenuOpen]);

  const MenuItem = ({ item, source, headerTransparent, activeItemId }) => {
    let itemId = item.id;
    let title = item.name;
    let slug = item.url;

    if (item.submenu && item.submenu.length > 0) {
      return (
        <MenuItemDropdown
          item={item}
          label={title}
          type={1}
          source={source}
          headerTransparent={headerTransparent}
          isActive={!(source && source == "mobilemenu") && item.id === activeItemId}
        />
      );
    }

    // activeClassName is kept for the mobile menu (whose behaviour must not
    // change) but it cannot be the desktop marker: Tailwind emits
    // `.text-neutral-200` AFTER `.text-brand-600`, so at equal specificity the
    // base colour won and the class was inert on every page. `rhi-nav-active`
    // is styled in custom.scss under `.rhi-site-header`, which outranks both
    // the base colour and the hover utility. Applied only to the desktop bar so
    // the mobilemenu branch stays byte-identical. Which item is active is
    // resolved once for the whole bar (resolveActiveItemId), not per item —
    // otherwise /financing/ lights up both Financing and About, since
    // Financing also sits inside the About dropdown.
    const isMobileMenu = !!(source && source == "mobilemenu");
    const isActive = !isMobileMenu && item.id === activeItemId;

    return (
      <Link
        to={`${slug && slug.length < 2 ? "/" : slug}`}
        activeClassName=" text-brand-600"
        className={`${isActive ? "rhi-nav-active " : ""}${
          source && source == "mobilemenu"
            ? "text-neutral-200 hover:text-white  block py-4 px-8 border-b border-white/20"
            : `px-2 py-7 whitespace-nowrap border-transparent     ${
                headerTransparent
                  ? "text-neutral-600 hover:text-neutral-900 "
                  : "text-neutral-200 hover:text-white    "
              }`
        }  font-semibold text-[15px] font-display text-base  transition-colors duration-150 ease-in md:border-b-[3px] md:border-transparent


        relative after:bg-brand-600 after:absolute after:h-[3px] after:w-0 after:bottom-[-3px] after:left-0 hover:after:w-full after:transition-all after:duration-300 cursor-pointer


        `}

      >
        {item.name}
      </Link>
    );
  };



  return (
    <StaticQuery
      query={graphql`
        query SiteTitleQueryInNav {
          site {
            siteMetadata {
              title
              description
              companyInfo {
                phone
                email
                addressLine1
                addressLine2
                city
                state
                zip
                license
              }
            }
          }
          allContentfulNavigation(filter: { name: { eq: "Main Navigation" } }) {
            edges {
              node {
                id
                name
                menuItems {
                  id
                  name
                  title
                  url
                  openInNewWindow
                  submenu {
                    id
                    name
                    menuItems {
                      id
                      name
                      title
                      url
                      openInNewWindow
                    }
                  }
                }
                campaign
              }
            }
          }
        }
      `}
      render={(data) => {
        const items = withServiceAreas(withSiteUrls(data.allContentfulNavigation.edges[0].node.menuItems));
        const activeItemId = resolveActiveItemId(pathname, items);
        return (
          <>

         <div className={` items-center ${" bg-222 block text-white"}  font-display  block relative z-20`}>
                <div className="flex justify-center md:justify-between items-center  font-display mx-auto py-2 text-xs md:text-sm max-w-7xl px-4 md:px-6">
                  <span className="block text-center md:text-left font-medium">#1 Roofing Experts Serving Michigan</span>
                  <span className=" hidden md:flex items-center text-center md:text-left font-medium">

                    <USAICON/>{subtagline}</span>
                </div>
              </div>
            {/* rhi-site-header is a stable styling hook, not a Tailwind class:
                accessibility-widget.css counter-scales this bar when the
                visitor turns up text size, and keying that off `.sticky.top-0`
                would break silently the next time these utilities are edited. */}
            <div className={`rhi-site-header sticky top-0 z-[20]   border-gray-800 ${headerTransparent ? "header--transparent-main" : ""}`}>

              <div
                className={`${headerTransparent ? " bg-white/80 backdrop-blur-lg -mb-[95px]  " : "bg-[#2b2b2b]"} ${
                  !shadow ? "" : "md:shadow-sm nav-shadow"
                } relative z-20 `}
              >
                {/* Spacing at xl is set per boundary, not with one uniform space-x.
                    The three gaps in this row are not interchangeable: the logo gap has
                    a hard minimum (see the nav comment below), the nav-to-CTA gap is
                    elastic and absorbs whatever is left, and a single space-x wide
                    enough for the logo would blow a width budget that seven nav items
                    already strain. Below xl the desktop nav is hidden and only the
                    logo/CTA boundary exists, so the md gap still governs there.

                    gap, not space-x: Tailwind's space-x-* is
                    `> :not([hidden]) ~ :not([hidden])` (specificity 0,3,0), so it beats
                    any plain ml-* utility on a child no matter what order they appear
                    in. The per-boundary margins below are silently dead unless the row
                    stops setting child margins at xl. */}
                <div className="rhi-header-row flex justify-between items-center px-4 md:px-6 md:gap-x-10 xl:gap-x-0 mx-auto max-w-7xl max-h-[94px] ">
                <div className="flex items-center logo-navbar-dark py-5 relative">

                  <Link to="/" className="block  -mx-2 -my-1 w-40 md:w-48 shrink-0" title={companyname}>
                    <Logo classes={"transition text-white"} singleColor={true} />
                  </Link>
                  </div>

                  {/* xl:ml-11 (44px) is a floor, not taste. `.logo-navbar-dark::after`
                      in custom.scss is the skewed red slab, and it is drawn 18px PAST
                      its own container (`right: -18px`) to make the angle — so the red
                      actually ends at (logo box right + 18px), not at the logo box.
                      Anything under ~42px here renders "Home" on top of the red; 44px
                      leaves 26px of measured clearance beyond the slab edge.

                      gap-x-[11px] is off the spacing scale because the budget is: 11px
                      is the widest even rhythm that still lands Free Estimate on the
                      same 24px gutter the page content uses at 1280px. Label-to-label
                      is 27px (11px gap + the items' own px-2), up from 20px.
                      md:space-x-4 used to sit here and never rendered — this container
                      is `hidden xl:flex`. */}
                  {mainnavigationstyle == "inline" ? (
                    <nav
                      aria-label="Main navigation"
                      className="items-center font-display hidden xl:flex xl:ml-11 xl:gap-x-[11px]"
                    >
                      {/* <nav>, not <div>: the classes and therefore the layout are
                          identical (display still comes from `hidden xl:flex`), but the
                          seven links now sit in a landmark. Turning the two dropdown
                          triggers into real anchors added them to axe's `region` rule,
                          which flags content outside any landmark; naming this row
                          fixes that and takes the pre-existing header nodes with it. */}
                      {items.map((item, i) => {
                        return (
                          <MenuItem key={item.id} item={item} type={item.type} headerTransparent={headerTransparent} activeItemId={activeItemId} />
                        );
                      })}
                    </nav>
                  ) : null}

                  <div className="flex-1"/>

                  {mainnavigationstyle == "stacked" ? (
                    <div className="hidden lg:block font-display text-center">
                      <span className="font-semibold block">{slogan}</span>
                      <span className="text-sm text-neutral-800 block">{subtagline}</span>
                    </div>
                  ) : null}

                 {/* xl:ml-4 is a floor on the divider gap: the flex-1 spacer above is
                     allowed to collapse to 0 at 1280px, and without this the border-l
                     rule would sit hard against "Financing". */}
                 <div className="rhi-header-actions flex space-x-1 md:space-x-2 items-center md:border-l border-neutral-600 xl:ml-4">
                 <div className="  font-display shrink-0">
                    <div className="flex flex-col sm:flex-row items-center">
                    {/* xl:pl-4 xl:pr-3 xl:mr-1 trim 20px of padding out of the phone
                        block at xl only. That 20px, plus the 15px that xl:text-lg takes
                        off the number below, is what pays for the nav rhythm above. At
                        md-lg the block keeps its original px-6 / mr-2. */}
                    <a
                          className={`mr-2 xl:mr-1 hidden md:block border-inset active:scale-[.97] transition px-4 py-3 md:py-3 md:px-6 xl:pl-4 xl:pr-3 text-sm  font-semibold text-sm rounded-lg ${
                            headerTransparent ? "text-neutral-900" : "text-neutral-900"
                          }`}
                          href={phoneHref}
                        ><div className="flex items-center text-neutral-400">Call us today!</div>
                        <span className="text-lg lg:text-xl xl:text-lg font-semibold text-white">{phonenumber}</span>
                        </a>
                      <div className="flex">
                      <Button
                          onClick={() => setContactFormOpen(true)}
                          aria-label="Open contact form"
                          className="hidden sm:block md:hidden mr-2 !bg-neutral-900"
 color="dark"
 >
                        <span className="text-[13px] lg:text-[15px] sm:text-sm font-medium hidden">Call</span> <PhoneIcon fill="white" className="h-4 w-4 sm:w-4 sm:h-4 ml-1"/>
                        </Button>
                        <Button
                          onClick={() => setContactFormOpen(true)}
                          className="rhi-header-cta whitespace-nowrap"
 color="brandGradient"
 >
                        <span className="text-[13px] lg:text-[15px] sm:text-sm font-medium">Free Estimate</span> <ChevronRight className="h-4 w-4 sm:w-4 sm:h-4"/>
                        </Button>

                        <ContactFormModal modalOpen={contactFormOpen} onModalClose={setContactFormOpen} />
                      </div>
                    </div>
                  </div>

                  <div className={`   -my-2 xl:hidden sm:pl-0 sm:m-0`}>

                  <Popover className="xl:hidden flex">
                {({ open }) => (
                  <>
                    <Popover.Button
                    className={`${
                      headerTransparent
                        ? "text-neutral-600"
                        : " text-neutral-100 hover:bg-neutral-800  focus:text-neutral-100  focus:bg-neutral-800"
                    } text-center p-2   rounded-lg  focus:outline-none  transition duration-150 ease-in  bg-black/20`}  aria-label="Toggle site navigation"
                    >
                      {({ open }) =>

open ? (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="h-7 w-7 mx-auto"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
) : (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="h-7 w-7 mx-auto"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
  </svg>
)


                      }
                    </Popover.Button>
                    {/* Always mounted; open/close is a pure CSS fade + 24px
                        slide (the same motion framer-motion's AnimatePresence
                        provided). visibility is in the transition list so the
                        panel stays visible through the fade-out, then leaves
                        the a11y tree and tab order once hidden.

                        rhi-mobile-menu is a stable styling hook, not a Tailwind
                        class: accessibility-widget.css narrows the header out of
                        the text-spacing and readable-font options because the bar
                        has no slack, and this panel is a full-width overlay that
                        does, so it opts back in by name. */}
                    <Popover.Panel
                      static
                      className={`rhi-mobile-menu absolute inset-x-0 top-[100%] z-0 origin-top rounded-b-2xl transition-[opacity,transform,visibility] duration-200 ${
                        open
                          ? "visible opacity-100 translate-y-0 ease-out pointer-events-auto"
                          : "invisible opacity-0 -translate-y-6 ease-in pointer-events-none"
                      }`}
                    >
                        <div className="flex h-full flex-col overflow-y-scroll bg-[#2b2b2b]/95 backdrop-blur-xl shadow-xl">
                          <div className="relative  font-display">
                            <div className="flex px-6 justify-between py-2  shadow-xl relative ">
                              <p className="text-white">Call Us Today!</p>
                              <a
                                className={`block font-bold  block text-brand-600 `}
                                href={phoneHref}
                              >
                                {phonenumber}
                              </a>
                            </div>
                            {" "}
                            <div className="overflow-auto max-h-[60dvh]">
                              {items.map((item, i) => {
                                return <MenuItem key={i} item={item} type={item.type} source={"mobilemenu"} activeItemId={activeItemId} />;
                              })}
                            </div>
                          </div>
                        </div>
                      </Popover.Panel>
                  </>
                )}
              </Popover>



                  </div>
                  </div></div>

              </div>

              {mainnavigationstyle == "stacked" ? (
                <div className="relative bg-white text-white md:shadow-sm md:border-none z-10 hidden md:block font-display">
                  <div className="flex items-center px-4 md:px-6 md:space-x-10 container mx-auto ">
                    {items.map((item, i) => {
                      return <MenuItem key={i} item={item} type={item.type} activeItemId={activeItemId} />;
                    })}
                  </div>
                </div>
              ) : null}
            </div>



            <div className="hidden bg-brand-700  text-neutral-100">
        {featureList && (
          <div className="mx-auto flex  items-center justify-center gap-3 py-3 md:gap-5">
            {featureList.map((item, i) => {
              return (
                <>
                  {" "}
                  {i > 0 && <span className="text-white">/</span>}
                  <div className="flex  flex-row  items-center" key={i}>
                    {item.icon && (
                      <item.icon
                        className="white-600 mr-2 hidden shrink-0 text-white sm:block"
                        size={16}
                      />
                    )}

                    <p className="text-left  text-xs sm:text-sm font-medium leading-6 text-white  ">
                      {item.item}
                    </p>
                  </div>
                </>
              )
            })}
          </div>
        )}
      </div>


          </>
        );
      }}
    />
  );
}

export default Navbar;
